import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toFileStoreError, type FileStoreApi } from './fileStoreApi';
import type { FileEntityRef, FileListQuery, FileListResponse } from './fileStoreTypes';

export const FILE_STORE_SEARCH_DEBOUNCE_MS = 300;

/** Files per page of the non-suggestion block; the route's default, sent explicitly so the page count is known. */
export const FILE_STORE_PAGE_SIZE = 50;

export type FileStoreFilters = {
  /** Type keys; ignored while the consumer forces `defaultTypeFilter`. */
  type: string[];
  year: number[];
  /** `null` = Siège social (sent as `entity[]=0`). */
  entity: (number | null)[];
  /** Provenance kinds (`upload`, `connector`, …). */
  provenance: string[];
};

export type FileStoreFilterKey = keyof FileStoreFilters;

export type FileStoreFilterValue<K extends FileStoreFilterKey> = FileStoreFilters[K][number];

export type UseFileStoreArgs = {
  api: FileStoreApi;
  ownerRecordId: number;
  wantedTypes: readonly string[];
  suggestYears: readonly number[];
  defaultTypeFilter?: string;
};

export type FileStoreListState =
  | { status: 'loading'; data: FileListResponse | null; error: null }
  | { status: 'ready'; data: FileListResponse; error: null }
  | { status: 'error'; data: FileListResponse | null; error: string };

export type UseFileStoreResult = {
  list: FileStoreListState;
  filters: FileStoreFilters;
  toggleFilter: <K extends FileStoreFilterKey>(key: K, value: FileStoreFilterValue<K>) => void;
  search: string;
  setSearch: (search: string) => void;
  /** Every entity label seen in a response, so a selected filter keeps its label once the facets narrow. */
  entityLabels: ReadonlyMap<number | null, string>;
  /** 1-based page of the non-suggestion block. */
  page: number;
  /** Pages the non-suggestion block spans (1 until a list answered). */
  pageCount: number;
  setPage: (page: number) => void;
  refresh: () => void;
};

const EMPTY_FILTERS: FileStoreFilters = { type: [], year: [], entity: [], provenance: [] };

const toggle = <T>(values: readonly T[], value: T): T[] => (values.includes(value)
  ? values.filter((current) => current !== value)
  : [...values, value]);

/**
 * List state of one owner's file store, as the picker shows it. Filters and the search go to
 * the server, which also splits suggestions from the rest: the answer is rendered as returned,
 * never re-split here. Only the latest request may land (a slow answer to an older filter set
 * is dropped). The non-suggestion block is paged: a new search or filter set starts again from
 * page 1, and a page left empty (its last file deleted) falls back to the last page that holds
 * files.
 */
export const useFileStore = ({
  api,
  ownerRecordId,
  wantedTypes,
  suggestYears,
  defaultTypeFilter,
}: UseFileStoreArgs): UseFileStoreResult => {
  const [filters, setFilters] = useState<FileStoreFilters>(EMPTY_FILTERS);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [reloadCount, setReloadCount] = useState(0);
  const [list, setList] = useState<FileStoreListState>({ status: 'loading', data: null, error: null });
  const [entityLabels, setEntityLabels] = useState<ReadonlyMap<number | null, string>>(() => new Map());
  const [paging, setPaging] = useState<{ scope: string; page: number }>({ scope: '', page: 1 });
  const requestSequence = useRef(0);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), FILE_STORE_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [search]);

  // Consumers pass array literals: key the query on their content, not their identity.
  const wantedKey = wantedTypes.join('\u0000');
  const suggestKey = suggestYears.join(',');
  const scopeQuery = useMemo<FileListQuery>(() => ({
    search: debouncedSearch,
    type: defaultTypeFilter === undefined || defaultTypeFilter === '' ? filters.type : [defaultTypeFilter],
    year: filters.year,
    entity: filters.entity,
    provenance: filters.provenance,
    wanted_type: wantedKey === '' ? [] : wantedKey.split('\u0000'),
    suggest_year: suggestKey === '' ? [] : suggestKey.split(',').map(Number),
  }), [debouncedSearch, defaultTypeFilter, filters, wantedKey, suggestKey]);
  // A page belongs to the owner, search and filters it was turned on: any change reads page 1.
  const scope = `${ownerRecordId}:${JSON.stringify(scopeQuery)}`;
  const page = paging.scope === scope ? paging.page : 1;
  const query = useMemo<FileListQuery>(
    () => ({ ...scopeQuery, page, per_page: FILE_STORE_PAGE_SIZE }),
    [scopeQuery, page],
  );

  useEffect(() => {
    requestSequence.current += 1;
    const sequence = requestSequence.current;
    setList((current) => ({ status: 'loading', data: current.data, error: null }));

    api.list(ownerRecordId, query).then((data) => {
      if (sequence !== requestSequence.current) {
        return;
      }
      const lastPage = Math.max(1, Math.ceil(data.total / FILE_STORE_PAGE_SIZE));
      if (data.files.length === 0 && page > lastPage) {
        setPaging({ scope, page: lastPage });
        return;
      }
      setEntityLabels((current) => {
        const next = new Map(current);
        const seen: FileEntityRef[] = [...data.facets.entities, ...(data.labels?.entities ?? [])];
        seen.forEach((entity) => next.set(entity.id, entity.label));
        return next;
      });
      setList({ status: 'ready', data, error: null });
    }, (error: unknown) => {
      if (sequence !== requestSequence.current) {
        return;
      }
      setList((current) => ({ status: 'error', data: current.data, error: toFileStoreError(error).message }));
    });
  }, [api, ownerRecordId, query, reloadCount, page, scope]);

  const toggleFilter = useCallback(<K extends FileStoreFilterKey>(key: K, value: FileStoreFilterValue<K>) => {
    setFilters((current) => ({ ...current, [key]: toggle<FileStoreFilterValue<K>>(current[key], value) }));
  }, []);

  const refresh = useCallback(() => setReloadCount((count) => count + 1), []);

  const setPage = useCallback((next: number) => setPaging({ scope, page: Math.max(1, next) }), [scope]);
  const total = list.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / FILE_STORE_PAGE_SIZE));

  return { list, filters, toggleFilter, search, setSearch, entityLabels, page, pageCount, setPage, refresh };
};
